import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { PlusCircle, MinusCircle, Share2, Star, Settings, ArrowLeftRight } from 'lucide-react';
import WatchlistAssignGroupsModal from '../WatchlistAssignGroupsModal';
import SharePreviewModal from '../SharePreviewModal';
import OptionsPremiumChartModal from '../OptionsPremiumChartModal';
import { saveContract, getWatchlistGroups, assignContractsToGroup } from '../../services/api';
import ScreenerFilterControls from '../ScreenerFilterControls';
import HeatmapSettingsPanel from './HeatmapSettingsPanel';
import ContractHeatmapCard from '../shared/ContractHeatmapCard';
import MobileHeatmapGrid from './MobileHeatmapGrid';
import MobileScreenerContextBar from './MobileScreenerContextBar';
import { getSimpleStrikePositionLabel } from '../../utils/presetMappings';
import { useLastClickedTicker } from '../../contexts/LastClickedTickerContext';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import '../../styles/MobileScreenerHeatmap.css';
import { mobileColors } from '../../theme/mobileTokens';
import {
  DEFAULT_SCENARIOS,
  DEFAULT_SCENARIOS_INTRADAY,
  ALL_SCENARIOS,
  STANDARD_SCENARIO_OPTIONS,
  computeHeatmapRow,
  getHeatmapCellColor,
  formatMultiplier,
  formatPremiumGain,
  formatScenarioLabel,
  formatScenarioPrice,
  formatScenarioLabelWithPrice,
  getUserDefaultScenarios,
  saveUserDefaultScenarios,
  clearUserDefaultScenarios,
} from '../../utils/elasticityCalculations';
import { findBreakevenPrice, getFractionalDaysToExpiry, INTRADAY_T_FLOOR_YEARS } from '../../utils/blackScholes';
import { getRowKey } from '../../utils/rowKey';
import { getIvTooltip } from '../../utils/dataSourceTooltip';

/**
 * Essential columns for the heatmap table (always visible, before scenario columns).
 * The first 6 columns (Ticker through Premium) are sticky, matching the screener table.
 * stickyIndex maps to the CSS class table-*-sticky-{N} (1-6).
 */
const ESSENTIAL_COLUMNS = [
  { key: 'Ticker', label: 'Ticker', width: '65px', stickyIndex: 1 },
  { key: 'Position Type', label: 'Position', width: '55px', stickyIndex: 2 },
  { key: 'Option Type', label: 'Type', width: '50px', stickyIndex: 3 },
  { key: 'Strike', label: 'Strike', width: '65px', stickyIndex: 4 },
  { key: 'Expiration', label: 'Expiration', width: '90px', stickyIndex: 5 },
  { key: 'Current Premium', label: 'Current\nPrem', width: '75px', stickyIndex: 6 },
  { key: 'Strike Position', label: 'Strike\nPosition', width: '105px' },
  { key: 'Underlying Price', label: 'Underlying', width: '95px' },
  { key: 'Implied Volatility', label: 'IV', width: '55px' },
  { key: 'DaysToExit', label: 'Days to\nExit', width: '60px' },
];

// getRowKey is imported from ../../utils/rowKey (null-safe, shared across the
// screener/watchlist tables and both heatmap views).

function ElasticityHeatmapView({
  results,
  // Shared state from ScreenerResponsive (persists across view switches)
  favoritedRows, setFavoritedRows,
  removedRows, setRemovedRows,
  showFavoritesOnly, setShowFavoritesOnly,
  selectedExpirations, setSelectedExpirations,
  selectedTickers, setSelectedTickers,
  selectedStrikePositions, setSelectedStrikePositions,
  sharedDaysToExit, setSharedDaysToExit,
  defaultDays,
  daysToExitMode, setDaysToExitMode,
  daysToExitPercent, setDaysToExitPercent,
  scenarioDefaults,
  // Intraday-only time-to-exit unit + slider scaling.
  daysToExitUnit = 'days', setDaysToExitUnit,
  maxFractionalDays = null,
  // Active screener mode ('default' for Basic/Intermediate, 'intraday' for the
  // Intraday mode). Scopes the heatmap's default scenario set + persistence key
  // and which toggle options the settings panel surfaces. Defaults to 'default'
  // so Basic/Intermediate are unaffected.
  screenerMode = 'default',
  // Underlying price shared by every row, so each scenario % can also show the
  // price it implies. Only the 0DTE module supplies it (one ticker per run);
  // null everywhere else, which renders the percentages alone as before.
  scenarioBasePrice = null,
  csvFileName = 'screener_heatmap_optionsLeveling.csv',
  isMobile = false,
  // 'detail' = card list (per-contract focus); 'heatmap' = sticky-grid matrix
  // (cross-contract comparison). Driven from ScreenerResponsive based on viewMode.
  mobileVariant = 'heatmap',
}) {
  // Resolve the mode-scoped scenario configuration once.
  const isIntraday = screenerMode === 'intraday';
  const scenarioPersistMode = isIntraday ? 'intraday' : 'default';
  const modeDefaultScenarios = isIntraday ? DEFAULT_SCENARIOS_INTRADAY : DEFAULT_SCENARIOS;
  const modeScenarioOptions = isIntraday ? ALL_SCENARIOS : STANDARD_SCENARIO_OPTIONS;
  // --- Mobile-only state ---
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const cardStripRefs = useRef([]);
  const isSyncingRef = useRef(false);
  // Show MobileScreenerContextBar the moment the .mhm-page-header banner first
  // reaches the viewport from below (i.e., as the user scrolls into the heatmap
  // section), and keep it visible while the user is anywhere at or past the
  // banner. Hidden only while the user is still on the form above the heatmap.
  const pageHeaderRef = useRef(null);
  const [scrolledPastHeader, setScrolledPastHeader] = useState(false);

  useEffect(() => {
    if (!isMobile) return;
    const target = pageHeaderRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(([entry]) => {
      setScrolledPastHeader(entry.boundingClientRect.top <= window.innerHeight);
    }, { threshold: 0 });
    observer.observe(target);
    return () => observer.disconnect();
  }, [isMobile, mobileVariant]);

  const registerStripRef = useCallback((idx, el) => {
    cardStripRefs.current[idx] = el;
  }, []);

  const handleStripScroll = useCallback((sourceIdx, scrollLeft) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    requestAnimationFrame(() => {
      const strips = cardStripRefs.current;
      for (let i = 0; i < strips.length; i++) {
        const el = strips[i];
        if (el && i !== sourceIdx && el.scrollLeft !== scrollLeft) {
          el.scrollLeft = scrollLeft;
        }
      }
      isSyncingRef.current = false;
    });
  }, []);
  // --- Heatmap settings ---
  // Initial selection comes from localStorage (user's persisted set) if any,
  // otherwise falls back to the shipped DEFAULT_SCENARIOS.
  const [selectedScenarios, setSelectedScenariosRaw] = useState(() => getUserDefaultScenarios(scenarioPersistMode));
  // Wrap the setter so every change is also persisted under the mode's key. The
  // wrapper supports both direct Set values and updater functions (the same
  // shape useState exposes).
  const setSelectedScenarios = useCallback((next) => {
    setSelectedScenariosRaw((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      if (resolved instanceof Set) {
        saveUserDefaultScenarios(resolved, scenarioPersistMode);
      }
      return resolved;
    });
  }, [scenarioPersistMode]);
  const [displayMode, setDisplayMode] = useState('premiumGain'); // 'multiplier' | 'premiumGain'
  // IV model for the projection (intraday only surfaces the toggle):
  // 'strike' = hold IV flat (sticky-strike), 'moneyness' = slide along the smile
  // as the underlying moves (sticky-moneyness). Defaults to sticky-moneyness for
  // intraday (recommended), sticky-strike for every other mode (unchanged).
  const [ivModel, setIvModel] = useState(isIntraday ? 'moneyness' : 'strike');
  // Earnings vol-crush modeling (intraday). Default on for intraday; the toggle
  // only surfaces when at least one contract actually has earnings in its window.
  const [volCrush, setVolCrush] = useState(isIntraday);
  const anyEarningsInWindow = useMemo(
    () => Array.isArray(results) && results.some(r => r && r._event && r._event.in_window),
    [results]
  );

  // --- Sort ---
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // --- Chart modal ---
  const [chartModalOpen, setChartModalOpen] = useState(false);
  // Scenario % carried from a cell tap; null when opened from the strike column.
  const [chartModalScenarioPct, setChartModalScenarioPct] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedContractIndex, setSelectedContractIndex] = useState(-1);
  const { setLastClickedTicker, setLastClickedContract } = useLastClickedTicker();

  // --- Share modal ---
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // --- Watchlist modal ---
  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [watchlistGroups, setWatchlistGroups] = useState([]);
  const [savedContractId, setSavedContractId] = useState(null);
  const [savedContractDetails, setSavedContractDetails] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savingRowKey, setSavingRowKey] = useState(null);

  // --- Days to exit: null = per-contract (use each row's Days to Gain), number = fixed override ---
  // sharedDaysToExit: null (per-contract) or number (user override)
  // Slider shows defaultDays as position when in per-contract mode
  const daysToExit = sharedDaysToExit; // null or number
  const sliderDays = daysToExit ?? defaultDays; // value shown on slider
  const [debouncedDays, setDebouncedDays] = useState(daysToExit);

  // Sync debouncedDays to sharedDaysToExit when the parent seeds it
  // externally — e.g. ScreenerResponsive's results effect fires after
  // ElasticityHeatmapView's first mount, so debouncedDays would otherwise
  // stay null and the heatmap (and context-bar chip) would render
  // per-contract values even though Fixed Days mode is active.
  // Only syncs when debouncedDays is currently null, so live slider drags
  // continue to flow through the 80ms debounce path in handleDaysChange.
  useEffect(() => {
    if (sharedDaysToExit != null && debouncedDays == null) {
      setDebouncedDays(sharedDaysToExit);
    }
  }, [sharedDaysToExit, debouncedDays]);
  const handleDaysChange = useCallback((val) => {
    // User moved slider → switch to fixed override mode
    setDaysToExitMode('fixed');
    setDaysToExitPercent(null);
    setSharedDaysToExit(val);
    clearTimeout(handleDaysChange._timer);
    handleDaysChange._timer = setTimeout(() => setDebouncedDays(val), 80);
  }, [setSharedDaysToExit, setDaysToExitMode, setDaysToExitPercent]);

  const handlePercentChange = useCallback((pct) => {
    setDaysToExitMode('percent');
    setDaysToExitPercent(pct);
    setSharedDaysToExit(null);
    setDebouncedDays(null);
  }, [setDaysToExitMode, setDaysToExitPercent, setSharedDaysToExit]);

  const handleHeatmapReset = useCallback(() => {
    // Reset to per-contract mode (null) + clear the user's persisted scenario
    // selection so we fall back to the shipped default set for this mode.
    setDaysToExitMode('per-contract');
    setSharedDaysToExit(null);
    setDaysToExitPercent(null);
    setDebouncedDays(null);
    clearUserDefaultScenarios(scenarioPersistMode);
    setSelectedScenariosRaw(new Set(modeDefaultScenarios));
  }, [setSharedDaysToExit, setDaysToExitMode, setDaysToExitPercent, scenarioPersistMode, modeDefaultScenarios]);

  // (Removed) the prior effect that reset selectedScenarios on every new queue
  // result. The user's persisted preference is now the source of truth and
  // should survive subsequent screener runs.

  // Sorted scenario list
  const scenarioArray = useMemo(
    () => Array.from(selectedScenarios).sort((a, b) => a - b),
    [selectedScenarios]
  );

  // --- Filtered data ---
  const filteredData = useMemo(() => {
    if (!Array.isArray(results)) return [];
    return results.filter(row => {
      if (!row || typeof row !== 'object') return false;
      const rowKey = getRowKey(row);
      return (
        (selectedExpirations.length === 0 || selectedExpirations.includes(row['Expiration'])) &&
        (selectedTickers.length === 0 || selectedTickers.includes(row['Ticker'])) &&
        (selectedStrikePositions.length === 0 || selectedStrikePositions.includes(row['Strike Position'])) &&
        !removedRows.has(rowKey) &&
        (!showFavoritesOnly || favoritedRows.has(rowKey))
      );
    });
  }, [results, selectedExpirations, selectedTickers, selectedStrikePositions, removedRows, showFavoritesOnly, favoritedRows]);

  // --- Max days from filtered data (nearest expiration sets the slider max) ---
  const maxDaysData = useMemo(() => {
    const dataToUse = filteredData.length > 0 ? filteredData : (Array.isArray(results) ? results : []);
    if (dataToUse.length === 0) return { days: 365, date: null };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const exps = dataToUse
      .map(r => r['Expiration'])
      .filter(e => e && e !== '-')
      .map(e => {
        const d = new Date(e);
        d.setHours(0, 0, 0, 0);
        const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
        return { days: diff, date: e };
      })
      .filter(x => x.days > 0)
      .sort((a, b) => a.days - b.days);

    if (exps.length === 0) return { days: 365, date: null };
    return { days: exps[0].days, date: exps[0].date };
  }, [filteredData, results]);

  // --- Compute heatmap values ---
  const heatmapData = useMemo(() => {
    return filteredData.map(row => {
      // Intraday: fractional time-to-expiry (sub-day) replaces the integer DTE.
      const fdte = isIntraday ? getFractionalDaysToExpiry(row['Expiration']) : null;
      const intradayCtx = isIntraday && Number.isFinite(fdte)
        ? { timeToExpiryDays: fdte, tFloorYears: INTRADAY_T_FLOOR_YEARS }
        : null;

      const isPercent = daysToExitMode === 'percent' && daysToExitPercent != null;
      const rowDaysToExit = isPercent
        ? (intradayCtx
            ? fdte * daysToExitPercent
            : Math.max(1, Math.round(parseFloat(row['Days Until Expiration']) * daysToExitPercent)))
        : debouncedDays;

      // Compute per-row breakeven using Black-Scholes binary search
      const dte = intradayCtx ? fdte : parseFloat(row['Days Until Expiration']);
      const strike = parseFloat(row['Strike']);
      const curPrem = parseFloat(row['Current Premium']);
      const rawIV = parseFloat(row['Implied Volatility']);
      const iv = rawIV > 5 ? rawIV / 100 : rawIV;
      const optType = row['Option Type']?.toLowerCase();
      const isSell = row['Position Type']?.toLowerCase() === 'sell';
      const floorYears = intradayCtx ? INTRADAY_T_FLOOR_YEARS : 0.0001;
      const effectiveDTE = rowDaysToExit != null
        ? rowDaysToExit
        : (parseFloat(row['Days to Gain']) || (intradayCtx ? dte * 0.35 : Math.max(1, Math.round(dte * 0.35))));
      const T = Math.max((dte - effectiveDTE) / 365, floorYears);
      const breakeven = findBreakevenPrice(strike, curPrem, T, iv, optType, isSell);

      return {
        ...row,
        _heatmap: computeHeatmapRow(
          row,
          isPercent ? null : debouncedDays,
          scenarioArray,
          isPercent ? daysToExitPercent : null,
          null,
          {
            ivSkew: row._iv_skew,
            stickyMoneyness: ivModel === 'moneyness',
            event: row._event,
            modelVolCrush: volCrush,
          },
          intradayCtx
        ),
        _daysToExit: rowDaysToExit,
        _breakeven: breakeven,
      };
    });
  }, [filteredData, debouncedDays, scenarioArray, daysToExitMode, daysToExitPercent, ivModel, volCrush, isIntraday]);

  // Default sort: Ticker asc → Expiration nearest-first → then, for intraday,
  // raw Strike ascending (options-chain order); other modes keep Strike Position
  // (deepest ITM → furthest OTM).
  const defaultSort = (a, b) => {
    // Ticker alphabetical
    const aTicker = String(a['Ticker'] || '').toLowerCase();
    const bTicker = String(b['Ticker'] || '').toLowerCase();
    if (aTicker < bTicker) return -1;
    if (aTicker > bTicker) return 1;
    // Expiration nearest first (YYYY-MM-DD string compare)
    const aExp = String(a['Expiration'] || '');
    const bExp = String(b['Expiration'] || '');
    if (aExp < bExp) return -1;
    if (aExp > bExp) return 1;
    // Intraday: ascending by raw strike (options-chain order).
    if (isIntraday) {
      const aStrike = parseFloat(a['Strike']) || 0;
      const bStrike = parseFloat(b['Strike']) || 0;
      return aStrike - bStrike;
    }
    // Other modes: Strike Position deepest ITM (most negative) → furthest OTM.
    const aPos = parseFloat(a['Strike Position']) || 0;
    const bPos = parseFloat(b['Strike Position']) || 0;
    return aPos - bPos;
  };

  // --- Sorted data ---
  const sortedData = useMemo(() => {
    const sortable = [...heatmapData];
    sortable.sort((a, b) => {
      // Tier 1: Favorites float to top
      const aFav = favoritedRows.has(getRowKey(a)) ? 0 : 1;
      const bFav = favoritedRows.has(getRowKey(b)) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;

      // Tier 2: Sort by selected column (or default if none selected)
      if (!sortConfig.key) return defaultSort(a, b);

      let aVal, bVal;

      // Check if sorting by a scenario column. Regex allows a decimal so
      // fractional intraday columns (e.g. scenario_0.5, scenario_-0.5) match;
      // parseFloat is identical to parseInt for the integer columns that
      // Basic/Intermediate produce, so their sorting is unchanged.
      const scenarioMatch = sortConfig.key.match(/^scenario_(-?\d+(?:\.\d+)?)$/);
      if (scenarioMatch) {
        const pct = parseFloat(scenarioMatch[1]);
        const aCell = a._heatmap?.[pct];
        const bCell = b._heatmap?.[pct];
        aVal = displayMode === 'multiplier' ? aCell?.multiplier : aCell?.premiumGain;
        bVal = displayMode === 'multiplier' ? bCell?.multiplier : bCell?.premiumGain;
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Date columns: compare as strings (YYYY-MM-DD sorts chronologically)
      if (sortConfig.key === 'Expiration') {
        const aStr = String(aVal || '');
        const bStr = String(bVal || '');
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }

      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortable;
  }, [heatmapData, sortConfig, favoritedRows, displayMode]);

  // --- Handlers ---
  const handleSort = (column) => {
    if (sortConfig.key === column) {
      setSortConfig({ key: column, direction: sortConfig.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSortConfig({ key: column, direction: 'asc' });
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return <FaSort className="inline ml-1 opacity-50" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="inline ml-1" />
      : <FaSortDown className="inline ml-1" />;
  };

  const handleToggleFavorite = (row) => {
    const key = getRowKey(row);
    setFavoritedRows(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleRemoveRow = (row) => {
    const key = getRowKey(row);
    setRemovedRows(prev => new Set([...prev, key]));
    setFavoritedRows(prev => { const next = new Set(prev); next.delete(key); return next; });
  };

  const handleSaveToWatchlist = async (row) => {
    const key = getRowKey(row);
    try {
      setIsSaving(true);
      setSavingRowKey(key);
      const contractData = {
        ticker: row['Ticker'],
        option_type: row['Option Type']?.toLowerCase(),
        strike: parseFloat(row['Strike']),
        expiration: row['Expiration'],
        average_cost_per_contract: parseFloat(row['Current Premium']) || undefined,
        days_to_gain: row['Days to Gain'] ? parseInt(row['Days to Gain']) : undefined,
        number_of_contracts: 1,
      };
      const response = await saveContract(contractData);
      const newContractId = response.data.id;
      setSavedContractId(newContractId);
      setSavedContractDetails({
        ticker: row['Ticker'],
        strike: row['Strike'],
        expiration: row['Expiration'],
        numberOfContracts: 1,
      });
      const groupsResponse = await getWatchlistGroups();
      setWatchlistGroups(groupsResponse.data || []);
      setShowWatchlistModal(true);
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Failed to save contract: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSaving(false);
      setSavingRowKey(null);
    }
  };

  const handleAssignToGroups = async (groupIds, mode) => {
    if (!savedContractId || groupIds.length === 0) return;
    try {
      await assignContractsToGroup(groupIds, [savedContractId], mode);
    } catch (error) {
      console.error('Error assigning to groups:', error);
    }
  };

  const fetchWatchlistGroups = async () => {
    try {
      const response = await getWatchlistGroups();
      setWatchlistGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  // Open the modal for a sortedData index — used by both handleTickerClick (initial open)
  // and the mobile pagination chips (prev/next neighbour).
  const openModalAtIndex = useCallback((index, defaultView = 'charts') => {
    if (index < 0 || index >= sortedData.length) return;
    const row = sortedData[index];
    const ticker = row['Ticker'];
    const optionType = row['Option Type']?.toLowerCase();
    const strike = row['Strike'];
    const expiration = row['Expiration'];
    if (!ticker || !optionType || !strike || !expiration) return;

    setSelectedContract({
      ticker,
      optionType,
      strike: parseFloat(strike),
      expiration,
      rowData: row,
      defaultView,
    });
    setSelectedContractIndex(index);
  }, [sortedData]);

  // Ticker click → open Premium & Underlying Charts modal (no explicit
  // scenario → modal falls back to localStorage-sticky scenario for Details).
  const handleTickerClick = (row) => {
    try {
      const idx = sortedData.findIndex(r =>
        r['Ticker'] === row['Ticker'] &&
        r['Option Type'] === row['Option Type'] &&
        r['Strike'] === row['Strike'] &&
        r['Expiration'] === row['Expiration']
      );
      if (idx === -1) return;
      openModalAtIndex(idx, 'charts');
      setChartModalScenarioPct(null);
      setChartModalOpen(true);
    } catch (error) {
      console.error('Error handling ticker click:', error);
    }
  };

  // Heatmap cell tap → open the same chart modal directly on the Details tab
  // with the tapped scenario pre-selected. Replaces the prior bottom-sheet.
  const handleCellTap = useCallback((row, pct) => {
    try {
      const cell = row._heatmap?.[pct];
      const raw = cell?.premiumGain;
      if (raw == null || isNaN(raw)) return; // no-data cell → no-op
      const idx = sortedData.findIndex(r =>
        r['Ticker'] === row['Ticker'] &&
        r['Option Type'] === row['Option Type'] &&
        r['Strike'] === row['Strike'] &&
        r['Expiration'] === row['Expiration']
      );
      if (idx === -1) return;
      openModalAtIndex(idx, 'details');
      setChartModalScenarioPct(Number(pct));
      setChartModalOpen(true);
    } catch (error) {
      console.error('Error handling cell tap:', error);
    }
  }, [sortedData, openModalAtIndex]);

  // Mobile pagination through neighbour contracts
  const formatStrikeLabel = (s) => {
    const num = parseFloat(s);
    if (isNaN(num)) return `$${s}`;
    return num % 1 === 0 ? `$${num.toFixed(0)}` : `$${num.toFixed(2)}`;
  };

  const handlePrevContract = useCallback(() => {
    if (selectedContractIndex > 0) {
      openModalAtIndex(selectedContractIndex - 1, selectedContract?.defaultView || 'charts');
    }
  }, [selectedContractIndex, openModalAtIndex, selectedContract]);

  const handleNextContract = useCallback(() => {
    if (selectedContractIndex >= 0 && selectedContractIndex < sortedData.length - 1) {
      openModalAtIndex(selectedContractIndex + 1, selectedContract?.defaultView || 'charts');
    }
  }, [selectedContractIndex, sortedData.length, openModalAtIndex, selectedContract]);

  const prevContractLabel = selectedContractIndex > 0
    ? `${formatStrikeLabel(sortedData[selectedContractIndex - 1]['Strike'])}${
        sortedData[selectedContractIndex - 1]['Option Type']?.toLowerCase() === 'put' ? 'P' : 'C'
      }`
    : '';
  const nextContractLabel = selectedContractIndex >= 0 && selectedContractIndex < sortedData.length - 1
    ? `${formatStrikeLabel(sortedData[selectedContractIndex + 1]['Strike'])}${
        sortedData[selectedContractIndex + 1]['Option Type']?.toLowerCase() === 'put' ? 'P' : 'C'
      }`
    : '';
  const positionLabel = selectedContractIndex >= 0
    ? `${selectedContractIndex + 1} of ${sortedData.length}`
    : '';

  // Current Premium click → open Options Dashboard
  const handlePremiumClick = (row) => {
    try {
      const ticker = row['Ticker'];
      const optionType = row['Option Type']?.toLowerCase();
      const strike = row['Strike'];
      const expiration = row['Expiration'];
      if (!ticker || !optionType || !strike || !expiration) return;

      setLastClickedTicker(ticker);
      setLastClickedContract({
        option_type: optionType,
        strike: parseFloat(strike),
        expiration: expiration,
      });
      const url = `/options-dashboard/${ticker}/${optionType}/${strike}/${expiration}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error handling premium click:', error);
    }
  };

  // --- Format essential column values ---
  const formatEssentialValue = (value, colKey, row) => {
    if (value === undefined || value === null || value === 'undefined') return '-';

    if (colKey === 'Strike') {
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return num % 1 === 0 ? `$${num.toFixed(0)}` : `$${num.toFixed(2)}`;
    }
    if (colKey === 'Current Premium' || colKey === 'Underlying Price') {
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return `$${num.toFixed(2)}`;
    }
    if (colKey === 'Implied Volatility') {
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      return `${num.toFixed(1)}%`;
    }
    if (colKey === 'Strike Position') {
      if (value !== undefined && value !== null) {
        return getSimpleStrikePositionLabel(value, row['Option Type']);
      }
      return '-';
    }
    if (colKey === 'DaysToExit') {
      // Percentage mode: compute per-row from expiration
      if (daysToExitMode === 'percent' && daysToExitPercent != null) {
        const dte = parseFloat(row['Days Until Expiration']);
        return dte ? Math.max(1, Math.round(dte * daysToExitPercent)) : '-';
      }
      // Fixed mode or per-contract mode
      const raw = debouncedDays !== null ? debouncedDays : row['Days to Gain'];
      if (raw === null || raw === undefined || raw === '') return '-';
      const num = parseFloat(raw);
      if (isNaN(num)) return '-';
      // Intraday hours/30-min holds are fractional days → round to 2 decimals;
      // whole-day (Days mode) and other screener modes render unchanged.
      return (isIntraday && daysToExitUnit !== 'days') ? num.toFixed(2) : num;
    }
    return value;
  };

  // --- CSV export data ---
  const csvData = useMemo(() => {
    return sortedData.map(row => {
      const csvRow = {};
      ESSENTIAL_COLUMNS.forEach(col => {
        if (col.key === 'DaysToExit') {
          if (daysToExitMode === 'percent' && daysToExitPercent != null) {
            const dte = parseFloat(row['Days Until Expiration']);
            csvRow['Days to Exit'] = dte ? Math.max(1, Math.round(dte * daysToExitPercent)) : '';
          } else {
            csvRow['Days to Exit'] = debouncedDays !== null ? debouncedDays : (row['Days to Gain'] || '');
          }
        } else {
          csvRow[col.label] = row[col.key];
        }
      });
      scenarioArray.forEach(pct => {
        const cell = row._heatmap?.[pct];
        const val = displayMode === 'multiplier' ? cell?.multiplier : cell?.premiumGain;
        csvRow[formatScenarioLabelWithPrice(pct, scenarioBasePrice)] = val != null ? val : '';
      });
      return csvRow;
    });
  }, [sortedData, scenarioArray, displayMode, debouncedDays, scenarioBasePrice]);

  const csvHeaders = useMemo(() => {
    const headers = ESSENTIAL_COLUMNS.map(col => ({
      label: col.key === 'DaysToExit' ? 'Days to Exit' : col.label,
      key: col.key === 'DaysToExit' ? 'Days to Exit' : col.label,
    }));
    scenarioArray.forEach(pct => {
      const label = formatScenarioLabelWithPrice(pct, scenarioBasePrice);
      headers.push({ label, key: label });
    });
    return headers;
  }, [scenarioArray, scenarioBasePrice]);

  // --- Share modal: heatmap-tailored layout ---
  // Combine identity columns into a single two-line "Contract" cell, drop IV,
  // attach heatmap colors per scenario cell, and add a grouped header band.
  const shareColumns = useMemo(() => {
    const formatStrike = (s) => {
      const num = parseFloat(s);
      if (isNaN(num)) return s;
      return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    };

    const cols = [
      {
        key: 'Contract',
        label: 'Contract',
        className: 'share-table-cell-multiline',
        render: (row) => (
          <>
            <div className="share-contract-line1">
              {row['Ticker']} {row['Position Type']} {row['Option Type']}
            </div>
            <div className="share-contract-line2">
              ${formatStrike(row['Strike'])} · {row['Expiration']}
            </div>
          </>
        ),
      },
      { key: 'Current Premium',  label: 'Premium' },
      { key: 'Strike Position',  label: 'Strike\nPosition' },
      { key: 'Underlying Price', label: 'Underlying' },
    ];
    scenarioArray.forEach(pct => {
      const price = formatScenarioPrice(pct, scenarioBasePrice);
      cols.push({
        key: `_scenario_${pct}`,
        label: price ? `${formatScenarioLabel(pct)}\n${price}` : formatScenarioLabel(pct),
      });
    });
    return cols;
  }, [scenarioArray, scenarioBasePrice]);

  const shareData = useMemo(() => {
    return sortedData.map(row => {
      const flat = {
        'Ticker': row['Ticker'],
        'Position Type': row['Position Type'],
        'Option Type': row['Option Type'],
        'Strike': row['Strike'],
        'Expiration': row['Expiration'],
        'Current Premium': row['Current Premium'],
        'Strike Position': row['Strike Position'] !== undefined && row['Strike Position'] !== null
          ? getSimpleStrikePositionLabel(row['Strike Position'], row['Option Type'])
          : '-',
        'Underlying Price': row['Underlying Price'],
      };
      scenarioArray.forEach(pct => {
        const cell = row._heatmap?.[pct];
        const raw = displayMode === 'multiplier' ? cell?.multiplier : cell?.premiumGain;
        flat[`_scenario_${pct}`] = raw != null
          ? (displayMode === 'multiplier' ? formatMultiplier(raw) : formatPremiumGain(raw))
          : '--';
        flat[`_scenario_${pct}_style`] = getHeatmapCellColor(raw, displayMode);
      });
      return flat;
    });
  }, [sortedData, scenarioArray, displayMode]);

  const shareCellStyleFn = useCallback((row, col) => {
    if (col.key && col.key.startsWith('_scenario_')) {
      return row[`${col.key}_style`] || null;
    }
    return null;
  }, []);

  const shareHeaderGroups = useMemo(() => ([
    { label: '', span: 4 },
    {
      label: 'UNDERLYING PRICE % CHANGE',
      span: scenarioArray.length,
      className: 'share-table-header-group-emphasis',
    },
  ]), [scenarioArray.length]);

  const shareSettingsHeader = useMemo(() => (
    <div className="share-sliders-preview">
      <div className="share-slider-row">
        <span className="share-slider-label">Days to Exit</span>
        <span className="share-slider-value">{debouncedDays !== null ? `${debouncedDays} days` : 'Per-contract (% of exp)'}</span>
      </div>
      <div className="share-slider-row">
        <span className="share-slider-label">Mode</span>
        <span className="share-slider-value">{displayMode === 'multiplier' ? 'Underlying Multiplier' : 'Premium Gain %'}</span>
      </div>
      <div className="share-settings-subtitle">
        <div><strong>Days to Exit</strong> is the simulated holding period from today — not the contract's expiration date.</div>
        <div>Column headers show <strong>underlying price % change</strong>. Cells show projected <strong>premium % change</strong>.</div>
      </div>
    </div>
  ), [debouncedDays, displayMode]);

  // --- Mobile summary header values ---
  const summaryData = useMemo(() => {
    let holdPeriod;
    if (daysToExitMode === 'percent' && daysToExitPercent != null) {
      holdPeriod = `${Math.round(daysToExitPercent * 100)}% of exp`;
    } else if (debouncedDays != null) {
      holdPeriod = `${debouncedDays} days`;
    } else {
      holdPeriod = 'Per-contract';
    }

    const mode = displayMode === 'multiplier' ? 'Multiplier' : 'Premium gain %';

    const uniqueTickers = Array.from(new Set(sortedData.map(r => r['Ticker'])));
    let underlying;
    if (uniqueTickers.length === 1) {
      const t = uniqueTickers[0];
      const price = parseFloat(sortedData[0]?.['Underlying Price']);
      underlying = `(${t}) ${!isNaN(price) ? `$${price.toFixed(2)}` : '--'}`;
    } else if (uniqueTickers.length > 1) {
      underlying = `${uniqueTickers.length} tickers`;
    } else {
      underlying = '--';
    }

    return { holdPeriod, mode, underlying };
  }, [daysToExitMode, daysToExitPercent, debouncedDays, displayMode, sortedData]);

  // --- Legend swatches: sample the actual palette so it stays in sync ---
  const legendItems = useMemo(() => {
    if (displayMode === 'multiplier') {
      return [
        { label: '< 2x', sample: 1 },
        { label: '2x', sample: 2 },
        { label: '5x', sample: 5 },
        { label: '10x', sample: 10 },
        { label: '20x', sample: 20 },
        { label: '50x+', sample: 60 },
      ];
    }
    return [
      { label: '≤ -50%', sample: -75 },
      { label: '-10%', sample: -10 },
      { label: '~0%', sample: 0 },
      { label: '+10%', sample: 10 },
      { label: '+50%', sample: 50 },
      { label: '≥ +200%', sample: 250 },
    ];
  }, [displayMode]);

  if (!Array.isArray(results) || results.length === 0) return null;

  // --- Mobile layout (variant: 'detail' = card list; 'heatmap' = sticky grid) ---
  if (isMobile) {
    const isHeatmapVariant = mobileVariant === 'heatmap';

    return (
      <div className="mhm-root">
        {/* Page header — title + subtitle. The previous ⋯ button is removed;
            settings/filters access lives in the sticky MobileScreenerContextBar
            at the bottom of the screen (right above the bottom nav).
            Classes (mhm-page-header / mhm-page-title / mhm-page-subtitle) let
            MobileScreenerHeatmap.css's landscape rules target these without
            relying on tree position. */}
        <div ref={pageHeaderRef} className="mhm-page-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '12px 14px 8px',
          background: mobileColors.heatmapBg,
        }}>
          <div>
            <div className="mhm-page-title" style={{ fontSize: 17, fontWeight: 600, color: mobileColors.textPrimary, lineHeight: 1.2 }}>
              {isHeatmapVariant ? 'Heatmap' : 'Detail'}
            </div>
            <div className="mhm-page-subtitle" style={{ fontSize: 11, color: mobileColors.textSecondary, marginTop: 2 }}>
              {summaryData.underlying}
            </div>
          </div>
        </div>

        {/* The previous "Scenario · N tickers" chip was removed — its content
            now lives in the sticky context bar at the bottom of the screen. */}

        {/* Cells toggle (Premium % / Multiplier) lives inside the settings
            sheet now — see the bottom-of-file mount below HeatmapSettingsPanel. */}

        {/* Count + favorites + share (CSV export is desktop-only) */}
        <div style={{ padding: '0 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: mobileColors.textSecondary }}>
            {sortedData.length} contract{sortedData.length === 1 ? '' : 's'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onClick={() => setShowFavoritesOnly(prev => !prev)}
              disabled={favoritedRows.size === 0 && !showFavoritesOnly}
              style={{
                background: showFavoritesOnly ? 'rgba(250,204,21,0.18)' : 'transparent',
                color: showFavoritesOnly ? '#facc15' : mobileColors.textSecondary,
                border: `0.5px solid ${showFavoritesOnly ? 'rgba(250,204,21,0.4)' : mobileColors.borderSubtle}`,
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 11,
                cursor: favoritedRows.size === 0 && !showFavoritesOnly ? 'not-allowed' : 'pointer',
                opacity: favoritedRows.size === 0 && !showFavoritesOnly ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Star size={11} fill={showFavoritesOnly || favoritedRows.size > 0 ? 'currentColor' : 'none'} />
              {favoritedRows.size > 0 ? favoritedRows.size : ''}
            </button>
            <button
              type="button"
              onClick={() => setShareModalOpen(true)}
              style={{
                background: 'transparent',
                color: mobileColors.textSecondary,
                border: `0.5px solid ${mobileColors.borderSubtle}`,
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 11,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Share2 size={11} />
              Share
            </button>
          </div>
        </div>

        {/* Sync hint — only for Heatmap variant (grid uses synced cell strips).
            Class mhm-sync-hint lets MobileScreenerHeatmap.css hide this entire
            row in landscape, where the scenario columns are already visible. */}
        {isHeatmapVariant && sortedData.length > 1 && (
          <div className="mhm-sync-hint" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '4px 14px 10px',
            fontSize: 11, color: mobileColors.textTertiary,
          }}>
            <ArrowLeftRight size={11} />
            <span>Swipe any row — all rows scroll together</span>
          </div>
        )}

        {/* Body: heatmap grid vs detail card list */}
        {isHeatmapVariant ? (
          <MobileHeatmapGrid
            rows={sortedData}
            scenarioArray={scenarioArray}
            scenarioBasePrice={scenarioBasePrice}
            displayMode={displayMode}
            onOpenKebab={(row) => {
              // Tapping the strike-column body opens the chart modal — same
              // target the previous kebab affordance used.
              handleTickerClick(row);
            }}
            favoritedRowKeys={favoritedRows}
            onToggleFavorite={handleToggleFavorite}
            onSaveToWatchlist={handleSaveToWatchlist}
            onRemoveRow={handleRemoveRow}
            savingRowKey={savingRowKey}
            onCellTap={handleCellTap}
          />
        ) : (
          <div className="mhm-card-list">
            {sortedData.map((row, idx) => {
              const rowKey = getRowKey(row);
              const isFav = favoritedRows.has(rowKey);
              const isCurrentlySaving = isSaving && savingRowKey === rowKey;

              return (
                <ContractHeatmapCard
                  key={rowKey}
                  row={row}
                  rowIndex={idx}
                  scenarioArray={scenarioArray}
                  scenarioBasePrice={scenarioBasePrice}
                  displayMode={displayMode}
                  registerStripRef={registerStripRef}
                  onStripScroll={handleStripScroll}
                  isFavorited={isFav}
                  isSaving={isCurrentlySaving}
                  onToggleFavorite={handleToggleFavorite}
                  onSaveToWatchlist={handleSaveToWatchlist}
                  onRemoveRow={handleRemoveRow}
                  onTickerClick={handleTickerClick}
                />
              );
            })}
          </div>
        )}

        {/* Slide-up sheet: settings + filters */}
        <div
          className={`mhm-sheet-backdrop ${isSheetOpen ? 'mhm-sheet-open' : ''}`}
          onClick={() => setIsSheetOpen(false)}
        />
        <div className={`mhm-sheet ${isSheetOpen ? 'mhm-sheet-open' : ''}`}>
          <div className="mhm-sheet-handle-bar" />
          <div className="mhm-sheet-header">
            <span className="mhm-sheet-title">Heatmap Settings & Filters</span>
            <button
              type="button"
              className="mhm-sheet-close"
              onClick={() => setIsSheetOpen(false)}
              aria-label="Close settings"
            >
              ×
            </button>
          </div>
          <div className="mhm-sheet-body">
            <HeatmapSettingsPanel
              maxDays={maxDaysData.days}
              maxExpirationDate={maxDaysData.date}
              daysToExit={sliderDays}
              onDaysChange={handleDaysChange}
              selectedScenarios={selectedScenarios}
              onScenariosChange={setSelectedScenarios}
              scenarioBasePrice={scenarioBasePrice}
              scenarioOptions={modeScenarioOptions}
              defaultScenarioSet={modeDefaultScenarios}
              showIvModelToggle={isIntraday}
              ivModel={ivModel}
              onIvModelChange={setIvModel}
              showVolCrushToggle={isIntraday && anyEarningsInWindow}
              volCrush={volCrush}
              onVolCrushChange={setVolCrush}
              showUnitToggle={isIntraday}
              daysToExitUnit={daysToExitUnit}
              onUnitChange={setDaysToExitUnit}
              maxFractionalDays={maxFractionalDays}
              contractCount={sortedData.length}
              defaultExpanded
              defaultDays={defaultDays}
              onReset={handleHeatmapReset}
              isOverridden={daysToExit !== null}
              daysToExitMode={daysToExitMode}
              onModeChange={setDaysToExitMode}
              daysToExitPercent={daysToExitPercent}
              onPercentChange={handlePercentChange}
            />

            {/* Cell Values toggle — was previously inline above the heatmap;
                lives in the sheet now so the top chrome stays compact. */}
            <div style={{
              background: '#1f2937',
              borderRadius: 8,
              padding: '12px 14px',
              marginBottom: 16,
            }}>
              <label style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#d1d5db',
                display: 'block',
                marginBottom: 8,
              }}>
                Cell Values
              </label>
              <div style={{
                display: 'flex',
                gap: 4,
                background: mobileColors.cardSurface,
                padding: 3,
                borderRadius: 8,
                border: `0.5px solid ${mobileColors.borderSubtle}`,
              }}>
                {['premiumGain', 'multiplier'].map((mode) => {
                  const isActive = displayMode === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setDisplayMode(mode)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: isActive ? mobileColors.accentBlueBg : 'transparent',
                        color: isActive ? mobileColors.accentBlue : mobileColors.textSecondary,
                        transition: 'all 0.15s',
                      }}
                    >
                      {mode === 'premiumGain' ? 'Premium %' : 'Multiplier'}
                    </button>
                  );
                })}
              </div>
            </div>

            <ScreenerFilterControls
              data={results}
              selectedExpirations={selectedExpirations}
              setSelectedExpirations={setSelectedExpirations}
              selectedTickers={selectedTickers}
              setSelectedTickers={setSelectedTickers}
              selectedStrikePositions={selectedStrikePositions}
              setSelectedStrikePositions={setSelectedStrikePositions}
            />
          </div>
        </div>

        {/* Shared modals (same as desktop) */}
        <OptionsPremiumChartModal
          isOpen={chartModalOpen}
          onClose={() => setChartModalOpen(false)}
          ticker={selectedContract?.ticker}
          optionType={selectedContract?.optionType}
          strike={selectedContract?.strike}
          expiration={selectedContract?.expiration}
          rowData={selectedContract?.rowData}
          defaultView={selectedContract?.defaultView || 'charts'}
          scenarioPct={chartModalScenarioPct}
          onPrev={selectedContractIndex > 0 ? handlePrevContract : undefined}
          onNext={selectedContractIndex >= 0 && selectedContractIndex < sortedData.length - 1 ? handleNextContract : undefined}
          prevLabel={prevContractLabel}
          nextLabel={nextContractLabel}
          positionLabel={positionLabel}
          onToggleFavorite={() => selectedContract?.rowData && handleToggleFavorite(selectedContract.rowData)}
          onSaveToWatchlist={() => selectedContract?.rowData && handleSaveToWatchlist(selectedContract.rowData)}
          onRemoveRow={() => {
            if (!selectedContract?.rowData) return;
            handleRemoveRow(selectedContract.rowData);
            setChartModalOpen(false);
          }}
          isFavorited={selectedContract?.rowData ? favoritedRows.has(getRowKey(selectedContract.rowData)) : false}
          isSaving={selectedContract?.rowData ? savingRowKey === getRowKey(selectedContract.rowData) : false}
        />
        <WatchlistAssignGroupsModal
          isOpen={showWatchlistModal}
          onClose={() => {
            setShowWatchlistModal(false);
            setSavedContractId(null);
            setSavedContractDetails(null);
          }}
          onAssign={handleAssignToGroups}
          groups={watchlistGroups}
          fetchGroups={fetchWatchlistGroups}
          contracts={savedContractId ? [savedContractId] : []}
          contractDetails={savedContractDetails ? [savedContractDetails] : []}
        />
        <SharePreviewModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          data={shareData}
          columns={shareColumns}
          settingsHeader={shareSettingsHeader}
          headerGroups={shareHeaderGroups}
          cellStyleFn={shareCellStyleFn}
          title="Share Screener Results"
        />

        {/* Sticky bottom bar — status chip + Heatmap & filter settings.
            Sits at bottom:64px (immediately above the MobileBottomNav).
            Uses sharedDaysToExit (not debouncedDays) so the chip reflects the
            live source-of-truth value, including the initial parent-seeded
            value before any debounce has fired. */}
        <MobileScreenerContextBar
          isMobile={isMobile}
          hasResults={sortedData.length > 0}
          scrolledPastHeader={scrolledPastHeader}
          daysToExitMode={daysToExitMode}
          daysToExit={sharedDaysToExit}
          daysToExitPercent={daysToExitPercent}
          onOpenSheet={() => setIsSheetOpen(true)}
        />
      </div>
    );
  }

  // --- Desktop layout (existing table) ---
  return (
    <div className="mt-10">
      {/* Heatmap Settings Panel */}
      <div data-tour="heatmap-settings">
      <HeatmapSettingsPanel
        maxDays={maxDaysData.days}
        maxExpirationDate={maxDaysData.date}
        daysToExit={sliderDays}
        onDaysChange={handleDaysChange}
        selectedScenarios={selectedScenarios}
        onScenariosChange={setSelectedScenarios}
        scenarioBasePrice={scenarioBasePrice}
        scenarioOptions={modeScenarioOptions}
        defaultScenarioSet={modeDefaultScenarios}
        showIvModelToggle={isIntraday}
        ivModel={ivModel}
        onIvModelChange={setIvModel}
        showVolCrushToggle={isIntraday && anyEarningsInWindow}
        volCrush={volCrush}
        onVolCrushChange={setVolCrush}
        showUnitToggle={isIntraday}
        daysToExitUnit={daysToExitUnit}
        onUnitChange={setDaysToExitUnit}
        maxFractionalDays={maxFractionalDays}
        contractCount={sortedData.length}
        defaultExpanded
        defaultDays={defaultDays}
        onReset={handleHeatmapReset}
        isOverridden={daysToExit !== null}
        daysToExitMode={daysToExitMode}
        onModeChange={setDaysToExitMode}
        daysToExitPercent={daysToExitPercent}
        onPercentChange={handlePercentChange}
      />
      </div>

      {/* Filters */}
      <ScreenerFilterControls
        data={results}
        selectedExpirations={selectedExpirations}
        setSelectedExpirations={setSelectedExpirations}
        selectedTickers={selectedTickers}
        setSelectedTickers={setSelectedTickers}
        selectedStrikePositions={selectedStrikePositions}
        setSelectedStrikePositions={setSelectedStrikePositions}
        dataTour="results-filters"
      />

      {/* Control bar: display mode toggle + favorites + export */}
      <div className="flex flex-wrap justify-between items-center mb-4" data-tour="heatmap-controls">
        {/* Display mode toggle */}
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setDisplayMode('multiplier')}
            className={`tab-button ${displayMode === 'multiplier' ? 'tab-selected' : 'tab-unselected'}`}
          >
            Underlying Multiplier
          </button>
          <button
            onClick={() => setDisplayMode('premiumGain')}
            className={`tab-button ${displayMode === 'premiumGain' ? 'tab-selected' : 'tab-unselected'}`}
          >
            Premium Gain %
          </button>
          {sortConfig.key && (
            <button
              onClick={() => setSortConfig({ key: null, direction: 'asc' })}
              className="flex items-center gap-1 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-[#218aff]/10 hover:text-[#218aff] hover:border-[#218aff]"
              title="Reset to default sort: Ticker → Expiration → Strike Position"
            >
              ↺ Default Sort
            </button>
          )}
        </div>

        <div className="flex gap-2 items-center">
          {/* Favorites toggle */}
          <button
            onClick={() => setShowFavoritesOnly(prev => !prev)}
            className={`flex items-center gap-1.5 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border ${
              showFavoritesOnly
                ? 'bg-yellow-400/20 text-yellow-400 border-yellow-400'
                : favoritedRows.size === 0
                  ? 'bg-[#1f2937] text-[#555] border-[#333] cursor-not-allowed'
                  : 'bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-yellow-400/10 hover:text-yellow-400 hover:border-yellow-400'
            }`}
            title={showFavoritesOnly ? 'Show all results' : 'Show favorites only'}
            disabled={favoritedRows.size === 0 && !showFavoritesOnly}
          >
            <Star className="w-3.5 h-3.5" fill={showFavoritesOnly || favoritedRows.size > 0 ? 'currentColor' : 'none'} />
            Favorites{favoritedRows.size > 0 ? ` (${favoritedRows.size})` : ''}
          </button>

          {/* Share */}
          <button
            onClick={() => setShareModalOpen(true)}
            className="btn-export-primary flex items-center gap-1"
            title="Share as image"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>

          {/* CSV Export */}
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={csvFileName}
            className="btn-export-primary"
          >
            Export CSV
          </CSVLink>
        </div>
      </div>

      {/* Heatmap Table */}
      <div className="table-container" data-tour="heatmap-table">
        <div className="table-scroll-container">
          <table className="table">
            <thead className="table-header">
              {/* Header group row */}
              <tr>
                <th className="table-header-cell-sticky table-header-cell-sticky-0" />
                {ESSENTIAL_COLUMNS.map((col) => {
                  const headerClass = col.stickyIndex
                    ? `table-header-cell-sticky table-header-cell-sticky-${col.stickyIndex}`
                    : 'table-header-cell';
                  return (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className={headerClass}
                      {...(col.key === 'DaysToExit' ? { 'data-tour-col': 'days-to-exit' } : {})}
                      style={{
                        maxWidth: col.width,
                        ...(col.key === 'DaysToExit' ? { borderRight: '2px solid #374151' } : {}),
                      }}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <span className="text-center whitespace-pre-line">{col.label}</span>
                        <span className="mt-1">{getSortIcon(col.key)}</span>
                      </div>
                    </th>
                  );
                })}
                {scenarioArray.map(pct => {
                  const scenarioPrice = formatScenarioPrice(pct, scenarioBasePrice);
                  return (
                    <th
                      key={pct}
                      onClick={() => handleSort(`scenario_${pct}`)}
                      className="table-header-cell"
                      style={{ minWidth: scenarioPrice ? '76px' : '60px', maxWidth: scenarioPrice ? '95px' : '75px' }}
                      data-tour-scenario={pct}
                      data-tooltip-id="heatmap-header-tooltip"
                      data-tooltip-content={pct === 0
                        ? `Shows the effect of time decay with no stock price movement${scenarioPrice ? ` (underlying stays at ${scenarioPrice})` : ''}`
                        : `Projected result if the underlying stock price ${pct > 0 ? 'rises' : 'drops'} ${Math.abs(pct)}%${scenarioPrice ? ` to ${scenarioPrice}` : ''}`}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <span className="text-center whitespace-nowrap">{formatScenarioLabel(pct)}</span>
                        {scenarioPrice && (
                          <span className="text-center whitespace-nowrap text-[10px] font-normal text-gray-400">
                            {scenarioPrice}
                          </span>
                        )}
                        <span className="mt-1">{getSortIcon(`scenario_${pct}`)}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {sortedData.map((row, idx) => {
                const rowKey = getRowKey(row);
                const isFavorited = favoritedRows.has(rowKey);
                const isCurrentlySaving = isSaving && savingRowKey === rowKey;

                return (
                  <tr
                    key={idx}
                    className={`${idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}${isFavorited ? ' table-row-favorited' : ''}`}
                  >
                    {/* Action buttons (sticky) */}
                    <td className="table-cell-sticky table-cell-sticky-0" data-tour="row-actions" style={{ padding: '2px 2px' }}>
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => handleToggleFavorite(row)}
                          className="w-4 h-4 rounded-full border border-gray-600 hover:border-yellow-400 hover:bg-yellow-400/20
                                     flex items-center justify-center transition-all duration-200 group"
                          title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Star
                            className={`w-3 h-3 transition-colors duration-200 ${
                              isFavorited ? 'text-yellow-400' : 'text-gray-400 group-hover:text-yellow-400'
                            }`}
                            fill={isFavorited ? 'currentColor' : 'none'}
                          />
                        </button>
                        <button
                          onClick={() => handleSaveToWatchlist(row)}
                          disabled={isCurrentlySaving}
                          className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#218aff] hover:bg-[#218aff]/20
                                     flex items-center justify-center transition-all duration-200 group disabled:opacity-50"
                          title="Save to Watchlist"
                        >
                          {isCurrentlySaving ? (
                            <span className="w-2.5 h-2.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <PlusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#218aff]" />
                          )}
                        </button>
                        <button
                          onClick={() => handleRemoveRow(row)}
                          className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#ef4444] hover:bg-[#ef4444]/20
                                     flex items-center justify-center transition-all duration-200 group"
                          title="Remove from results"
                        >
                          <MinusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#ef4444]" />
                        </button>
                      </div>
                    </td>

                    {/* Essential columns */}
                    {ESSENTIAL_COLUMNS.map((col) => {
                      const value = col.key === 'DaysToExit'
                        ? (debouncedDays !== null ? debouncedDays : row['Days to Gain'])
                        : row[col.key];
                      const formatted = formatEssentialValue(value, col.key, row);
                      const cellClass = col.stickyIndex
                        ? `table-cell-sticky table-cell-sticky-${col.stickyIndex}`
                        : 'table-cell';

                      // Render interactive cells for Ticker, Current Premium, Underlying Price
                      let cellContent;
                      if (col.key === 'Ticker') {
                        cellContent = (
                          <button
                            onClick={() => handleTickerClick(row)}
                            className="text-white hover:text-[#218aff] hover:underline transition font-medium flex items-center gap-1"
                            title="View Premium & Underlying Charts"
                          >
                            {formatted} ↗
                          </button>
                        );
                      } else if (col.key === 'Current Premium') {
                        cellContent = (
                          <button
                            onClick={() => handlePremiumClick(row)}
                            className="text-white hover:text-[#218aff] hover:underline transition flex items-center gap-1"
                            title="Open Options Dashboard"
                          >
                            {formatted} ↗
                          </button>
                        );
                      } else if (col.key === 'Underlying Price') {
                        cellContent = (
                          <Link
                            to={`/dashboard/${row['Ticker']}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white hover:text-[#218aff] hover:underline transition"
                            title="Open Ticker Dashboard"
                          >
                            {formatted} ↗
                          </Link>
                        );
                      } else if (col.key === 'Implied Volatility') {
                        // Hover: app IV vs Massive IV and which one the cells use.
                        const ivTip = getIvTooltip(row);
                        cellContent = ivTip ? (
                          <span
                            className="iv-source-hint"
                            data-tooltip-id="heatmap-cell-tooltip"
                            data-tooltip-content={ivTip}
                          >
                            {formatted}
                          </span>
                        ) : formatted;
                      } else {
                        cellContent = formatted;
                      }

                      return (
                        <td
                          key={col.key}
                          className={`${cellClass} table-cell-number`}
                          {...(col.key === 'DaysToExit' ? { 'data-tour-col': 'days-to-exit' } : {})}
                          style={{
                            maxWidth: col.width,
                            ...(col.key === 'DaysToExit' ? { borderRight: '2px solid #374151' } : {}),
                          }}
                        >
                          {cellContent}
                        </td>
                      );
                    })}

                    {/* Scenario columns with heatmap coloring */}
                    {scenarioArray.map(pct => {
                      const cell = row._heatmap?.[pct];
                      const rawValue = displayMode === 'multiplier' ? cell?.multiplier : cell?.premiumGain;
                      const formatted = displayMode === 'multiplier'
                        ? formatMultiplier(rawValue)
                        : formatPremiumGain(rawValue);
                      const cellStyle = getHeatmapCellColor(rawValue, displayMode);

                      const curUnd = parseFloat(row['Underlying Price']);
                      const curPrem = parseFloat(row['Current Premium']);
                      const simUnd = cell?.simulatedStock;
                      const simPrem = cell?.projectedPremium;

                      const breakeven = row._breakeven;

                      const fmtD = (v) => v != null && !isNaN(v) ? `$${v.toFixed(2)}` : '--';
                      const tooltipHtml =
                        `<div style="display:flex;flex-direction:column;gap:2px">` +
                        `<div style="display:flex;justify-content:space-between;gap:12px"><span style="color:#9ca3af">Premium:</span><span>${fmtD(curPrem)} → ${fmtD(simPrem)}</span></div>` +
                        `<div style="display:flex;justify-content:space-between;gap:12px"><span style="color:#9ca3af">Underlying:</span><span>${fmtD(curUnd)} → ${fmtD(simUnd)}</span></div>` +
                        (breakeven != null ? `<div style="display:flex;justify-content:space-between;gap:12px;border-top:1px solid #374151;padding-top:2px;margin-top:1px"><span style="color:#9ca3af">Breakeven:</span><span>${fmtD(breakeven)}</span></div>` : '') +
                        `</div>`;

                      return (
                        <td
                          key={pct}
                          className="table-cell text-center font-medium whitespace-nowrap"
                          style={{ ...cellStyle, minWidth: '60px', cursor: 'help' }}
                          data-tour-scenario={pct}
                          data-tooltip-id="heatmap-cell-tooltip"
                          data-tooltip-html={tooltipHtml}
                        >
                          {formatted}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="flex justify-between items-center text-sm text-gray-400 mt-3">
        <div>
          Showing {sortedData.length} of {results.length} contracts
        </div>
        <div className="text-xs">
          {displayMode === 'multiplier' ? (
            <>
              <span className="inline-block w-4 h-4 mr-1 rounded align-middle" style={{ backgroundColor: 'rgba(34, 197, 94, 0.3)' }} />
              Higher multiplier = more leverage
            </>
          ) : (
            <>
              <span className="inline-block w-4 h-4 mr-1 rounded align-middle" style={{ backgroundColor: '#1D9E75' }} />
              Green = positive return
              <span className="inline-block w-4 h-4 mr-1 ml-3 rounded align-middle" style={{ backgroundColor: '#F09595' }} />
              Red = negative return
            </>
          )}
        </div>
      </div>

      {/* Premium & Underlying Charts Modal */}
      <OptionsPremiumChartModal
        isOpen={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        ticker={selectedContract?.ticker}
        optionType={selectedContract?.optionType}
        strike={selectedContract?.strike}
        expiration={selectedContract?.expiration}
        rowData={selectedContract?.rowData}
        defaultView={selectedContract?.defaultView || 'charts'}
        scenarioPct={chartModalScenarioPct}
        onToggleFavorite={() => selectedContract?.rowData && handleToggleFavorite(selectedContract.rowData)}
        onSaveToWatchlist={() => selectedContract?.rowData && handleSaveToWatchlist(selectedContract.rowData)}
        onRemoveRow={() => {
          if (!selectedContract?.rowData) return;
          handleRemoveRow(selectedContract.rowData);
          setChartModalOpen(false);
        }}
        isFavorited={selectedContract?.rowData ? favoritedRows.has(getRowKey(selectedContract.rowData)) : false}
        isSaving={selectedContract?.rowData ? savingRowKey === getRowKey(selectedContract.rowData) : false}
      />

      {/* Watchlist Assignment Modal */}
      <WatchlistAssignGroupsModal
        isOpen={showWatchlistModal}
        onClose={() => {
          setShowWatchlistModal(false);
          setSavedContractId(null);
          setSavedContractDetails(null);
        }}
        onAssign={handleAssignToGroups}
        groups={watchlistGroups}
        fetchGroups={fetchWatchlistGroups}
        contracts={savedContractId ? [savedContractId] : []}
        contractDetails={savedContractDetails ? [savedContractDetails] : []}
      />

      {/* Share Preview Modal */}
      <SharePreviewModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        data={shareData}
        columns={shareColumns}
        settingsHeader={shareSettingsHeader}
        headerGroups={shareHeaderGroups}
        cellStyleFn={shareCellStyleFn}
        title="Share Screener Results"
      />

      {/* Scenario Column Header Tooltips */}
      <ReactTooltip
        id="heatmap-header-tooltip"
        place="top"
        delayShow={300}
        delayHide={100}
        style={{
          backgroundColor: '#111827',
          color: '#ffffff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '13px',
          maxWidth: '280px',
          lineHeight: '1.4',
          zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          border: '1px solid #374151',
        }}
      />

      {/* Heatmap Cell Tooltips (simulated/current underlying & premium) */}
      <ReactTooltip
        id="heatmap-cell-tooltip"
        place="top"
        delayShow={200}
        delayHide={50}
        style={{
          backgroundColor: '#111827',
          color: '#ffffff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '12px',
          maxWidth: '260px',
          lineHeight: '1.6',
          zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          border: '1px solid #374151',
          whiteSpace: 'pre-line',
        }}
      />
    </div>
  );
}

export default ElasticityHeatmapView;
